import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { StrategyFilters } from '../types/strategy';

const STORAGE_KEY = 'sparkadmin_strategy_filters';

const DEFAULT_FILTERS: StrategyFilters = {
  categoryId: '',
  status: '',
  page: 1,
  pageSize: 25,
  sortBy: 'sortOrder',
  sortOrder: 'asc',
};

function parseFiltersFromURL(searchParams: URLSearchParams): Partial<StrategyFilters> {
  const filters: Partial<StrategyFilters> = {};

  const categoryId = searchParams.get('categoryId');
  if (categoryId) filters.categoryId = categoryId;

  const status = searchParams.get('status');
  if (status && ['published', 'archived'].includes(status)) {
    filters.status = status as StrategyFilters['status'];
  }

  const page = searchParams.get('page');
  if (page) filters.page = parseInt(page, 10);

  const pageSize = searchParams.get('pageSize');
  if (pageSize) filters.pageSize = parseInt(pageSize, 10);

  const sortBy = searchParams.get('sortBy');
  if (sortBy && ['title', 'sortOrder', 'createdAt', 'category'].includes(sortBy)) {
    filters.sortBy = sortBy as StrategyFilters['sortBy'];
  }

  const sortOrder = searchParams.get('sortOrder');
  if (sortOrder && ['asc', 'desc'].includes(sortOrder)) {
    filters.sortOrder = sortOrder as StrategyFilters['sortOrder'];
  }

  return filters;
}

function filtersToSearchParams(filters: StrategyFilters): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.categoryId) params.set('categoryId', filters.categoryId);
  if (filters.status) params.set('status', filters.status);
  if (filters.page !== 1) params.set('page', filters.page.toString());
  if (filters.pageSize !== 25) params.set('pageSize', filters.pageSize.toString());
  if (filters.sortBy !== 'sortOrder') params.set('sortBy', filters.sortBy);
  if (filters.sortOrder !== 'asc') params.set('sortOrder', filters.sortOrder);

  return params;
}

function loadFiltersFromStorage(): Partial<StrategyFilters> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function saveFiltersToStorage(filters: StrategyFilters): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // Ignore storage errors
  }
}

export function useStrategyFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<StrategyFilters>(() => {
    const urlFilters = parseFiltersFromURL(searchParams);
    const storageFilters = loadFiltersFromStorage();
    return {
      ...DEFAULT_FILTERS,
      ...storageFilters,
      ...urlFilters,
    };
  });

  const updateFilter = useCallback(
    (key: keyof StrategyFilters, value: StrategyFilters[keyof StrategyFilters]) => {
      setFilters((prev) => {
        const updated = { ...prev, [key]: value };
        if (key !== 'page') {
          updated.page = 1;
        }
        const params = filtersToSearchParams(updated);
        setSearchParams(params, { replace: true });
        saveFiltersToStorage(updated);
        return updated;
      });
    },
    [setSearchParams]
  );

  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setSearchParams(new URLSearchParams(), { replace: true });
    saveFiltersToStorage(DEFAULT_FILTERS);
  }, [setSearchParams]);

  const hasActiveFilters =
    filters.categoryId !== '' || filters.status !== '';

  return {
    filters,
    updateFilter,
    clearFilters,
    hasActiveFilters,
  };
}
